import axios from 'axios';
import { useMemo } from 'react';
import { DEFAULT_API_BASE } from '../config';
import { extractApiErrorMessage } from '../utils/apiErrorUtils';
import { isAccessBlockedResponse } from '../utils/authResponseUtils';
import { useAuth } from '../providers/auth-provider';
import { useConfig } from '../providers/config-provider';

const normalizeBase = (value) => {
  const trimmed = (value || '').trim().replace(/\/+$/, '');
  return trimmed.length ? trimmed : DEFAULT_API_BASE.replace(/\/+$/, '');
};

export const loginRequest = (apiBase, email, password, rememberMe) => {
  const base = normalizeBase(apiBase);
  const payload = { email, password };

  if (typeof rememberMe === 'boolean') {
    payload.rememberMe = rememberMe;
  }

  return axios.post(`${base}/api/v1/auth/login`, payload, { withCredentials: true });
};

export const fetchPublicInfo = (apiBase) => {
  const base = normalizeBase(apiBase);
  return axios.get(`${base}/api/public/info`);
};

export const forgotPasswordRequest = (apiBase, email) => {
  const base = normalizeBase(apiBase);
  return axios.post(`${base}/api/v1/auth/forgot-password`, { email });
};

export const verifyOtpRequest = (apiBase, email, otp) => {
  const base = normalizeBase(apiBase);
  return axios.post(`${base}/api/v1/auth/verify-otp`, { email, otp });
};

export const resetPasswordRequest = (apiBase, email, otp, newPassword) => {
  const base = normalizeBase(apiBase);
  return axios.post(`${base}/api/v1/auth/reset-password`, { email, otp, newPassword });
};

export const useApiClient = () => {
  const { apiBase, companyId } = useConfig();
  const { markSessionExpired, markAccessBlocked } = useAuth();

  const client = useMemo(() => {
    const instance = axios.create({
      baseURL: normalizeBase(apiBase),
      withCredentials: true
    });

    instance.interceptors.request.use((config) => {
      const headers = { ...(config.headers || {}) };
      if (companyId) {
        headers['X-Company-Id'] = companyId;
      }
      return { ...config, headers };
    });

    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.config?.skipAuthHandling) {
          return Promise.reject(error);
        }

        if (isAccessBlockedResponse(error)) {
          markAccessBlocked(
            extractApiErrorMessage(
              error,
              'O acesso operacional está bloqueado no momento. Regularize a assinatura para continuar.',
            ),
          );
          return Promise.reject(error);
        }

        if (error?.response?.status === 401) {
          markSessionExpired(
            extractApiErrorMessage(
              error,
              'Sua sessão expirou. Faça login novamente para continuar.',
            ),
          );
        }
        return Promise.reject(error);
      }
    );

    return instance;
  }, [apiBase, companyId, markAccessBlocked, markSessionExpired]);

  return client;
};

export const buildRestaurantPath = (restaurantId, path) => {
  if (!restaurantId) {
    throw new Error('Restaurant id is required');
  }
  const normalized = path.startsWith('/') ? path.slice(1) : path;
  return `/api/v1/restaurants/${restaurantId}/${normalized}`;
};
